import {
  Milliseconds,
  SaveableTranscript,
  Speaker,
  Topic,
  TopicTag,
  Transcript,
  TranscriptItem
} from './transcription.api'
import { UITranscript } from './types/transcript'
import {
  GlossaryOfTermsItem,
  Tag,
  TagCategory,
  TextAnalysisItem,
  UITag,
  UITopic
} from './types/tag'
import { tagsColorPalette } from './store/modules/tagsPicker'
import { groupSimilarAnalysisTags, groupSimilarTags } from './utils/tags'
import { v4 as uuidv4 } from 'uuid'
import { UISpeaker, Word } from './models/word'

export const punctuanions = [',', '.', '!', '?', ':']

export const isPunctuationMark = (str: string): boolean => {
  if (str && str.length === 1) {
    return punctuanions.includes(str)
  } else {
    return false
  }
}

export const excludeEndingPunctuation = (str: string): string => {
  while (isPunctuationMark(str[str.length - 1])) {
    str = str.slice(0, str.length - 1)
  }

  return str
}

export const normalizePunctuation = (str: string): string => {
  if (isPunctuationMark(str)) {
    return str
  }

  let newStr = ''
  for (let i = 0; i < str.length; i++) {
    if (isPunctuationMark(str[i]) && isPunctuationMark(str[i + 1])) {
      newStr += ''
    } else {
      newStr += str[i]
    }
  }

  return newStr
}

export const removeWhitespaces = (str: string): string => {
  return str.replace(/\s/g, '')
}

export const getSubstringsFromString = (
  searchStr: string,
  str: string,
  caseSensitive?: boolean
): number[] => {
  const searchStrLen = searchStr.length,
    indexes: any[] = []
  if (searchStrLen == 0) {
    return []
  }

  let startIndex = 0,
    index
  if (!caseSensitive) {
    str = str.toLowerCase()
    searchStr = searchStr.toLowerCase()
  }

  while ((index = str.indexOf(searchStr, startIndex)) > -1) {
    indexes.push(index)
    startIndex = index + searchStrLen
  }

  return indexes
}

export const mapSpeakers = (
  transcript: Transcript | undefined
): UISpeaker[] => {
  const speakers: UISpeaker[] = []

  if (!transcript) {
    return speakers
  }

  transcript.parts.forEach((n: TranscriptItem, index: number) => {
    if (!speakers.find((speaker: UISpeaker) => speaker.name === n.speaker)) {
      speakers.push({
        name: n.speaker,
        id: index + 1,
        color: tagsColorPalette[index % tagsColorPalette.length]
      })
    }
  })

  return speakers
}

export const mapUITranscriptSpeakers = (
  transcript: UITranscript | null
): UISpeaker[] => {
  const speakers: UISpeaker[] = []

  if (!transcript) {
    return speakers
  }

  transcript.parts.forEach((part: Word) => {
    if (
      !speakers.find((speaker: UISpeaker) => speaker.name === part.speaker.name)
    ) {
      speakers.push(part.speaker)
    }
  })

  return speakers
}

export const transcriptToUI = (transcript: Transcript): UITranscript => {
  const speakers: UISpeaker[] = mapSpeakers(transcript)

  const words: any = transcript.words.map(
    (word: TranscriptItem, index: number) => ({
      ...word,
      index,
      id: uuidv4(),
      isPunctuation: isPunctuationMark(word.text),
      isSpaceRequired:
        transcript.words[index + 1] &&
        !isPunctuationMark(transcript.words[index + 1].text),
      speaker: {
        ...speakers.find(s => s.name === word.speaker)
      },
      version: 0
    })
  )

  const partWords: [Word[]] = [[]]
  let partIndex = 0

  words.reduce((previousSpeaker, word: Word) => {
    if (word.speaker.name === previousSpeaker) {
      partWords[partIndex].push(word)
      return previousSpeaker
    }

    partIndex++
    partWords[partIndex] = [word]

    return word.speaker.name
  }, words[0].speaker.name)

  let endWordIndex = -1

  const parts = transcript.parts.map((part: TranscriptItem, index) => {
    const startWordIndex = endWordIndex + 1
    const partId = uuidv4()

    let lettersCount = 0
    let curPartWords = []
    if (partWords[index]) {
      partWords[index].forEach((word: Word, index) => {
        word.partIndex = index
        word.partId = partId
        lettersCount += word.text.length
      })
      curPartWords = partWords[index]
      endWordIndex += partWords[index].length
    }

    return {
      ...part,
      id: partId,
      index,
      startWordIndex,
      endWordIndex,
      words: curPartWords,
      lettersCount,
      version: 0,
      speaker: {
        ...speakers.find(s => s.name === part.speaker)
      }
    }
  }) as Word[]

  return {
    ...transcript,
    id: transcript.id || 0,
    version: transcript.version || 0,
    words,
    parts,
    sentences: transcript.sentences.map((sentence: TranscriptItem) => ({
      ...sentence,
      speaker: {
        ...speakers.find(s => s.name === sentence.speaker)
      }
    })) as Word[]
  }
}

export const transcriptToApi = (
  transcript: UITranscript
): SaveableTranscript => {
  const words: TranscriptItem[] = []
  const parts = transcript.parts.map((part: Word) => {
    if (part.words) {
      part.words.forEach((word: Word) => {
        words.push({
          start: word.start,
          end: word.end,
          text: word.text,
          speaker: part.speaker.name as Speaker
        })
      })
    }

    return {
      start: part.start,
      end: part.end,
      text: '...',
      speaker: part.speaker.name
    }
  }) as TranscriptItem[]

  return {
    id: transcript.id,
    version: 0,
    words
  }
}

export const topicTagToApi = (tag: UITag): TopicTag => {
  return {
    id: tag.id,
    transcriptId: tag.transcriptId,
    transcriptVersion: tag.transcriptVersion,
    createdAt: tag.createdAt,
    editedAt: tag.editedAt,
    createdBy: tag.createdBy,
    editedBy: tag.editedBy,
    type: tag.type,
    category: tag.category,
    subCategory: tag.subCategory,
    startWordIndex: tag.startWordIndex,
    endWordIndex: tag.endWordIndex,
    startWordIndexPart: tag.startWordIndexPart,
    endWordIndexPart: tag.endWordIndexPart,
    partIndex: -1,
    speaker: tag.speaker,
    note: tag.note,
    flagged: tag.flagged,
    deleted: tag.deleted
  }
}

export function topicTagsToApi(tags: UITag[]): TopicTag[] {
  return tags.map(topicTagToApi)
}

export function removeLastCharacter(str: string, length?: number): string {
  const removeLength = length || 1
  return str.substring(0, str.length - removeLength)
}

export const removeTag = (
  category: TagCategory,
  _tag: Tag,
  index?: number
): Tag[] => {
  return category.tags
    .map((tag: Tag) => {
      if (tag.id !== _tag.id) {
        return tag
      }

      if (tag.wordIndices) {
        if (index !== undefined) {
          const newIndices = tag.wordIndices.filter(
            i => !(index >= i.startWordIndex && index <= i.endWordIndex)
          )

          if (!newIndices.length) {
            return tag
          }

          if (newIndices.length === 1) {
            tag.startWordIndex = tag.wordIndices[0].startWordIndex
            tag.endWordIndex = tag.wordIndices[0].endWordIndex
            tag.wordIndices = undefined
            return tag
          }

          tag.wordIndices = newIndices
        } else {
          // Clear all tags
          tag.wordIndices = undefined
        }

        return tag
      }

      tag.startWordIndex = 0
      tag.endWordIndex = 0
      return tag
    })
    .filter((tag: Tag) => tag.startWordIndex !== 0 && tag.endWordIndex !== 0)
}

export function isTextNode(node: Node): boolean {
  return node.nodeType === 3
}

export function isWordElement(element: HTMLElement | null): boolean {
  return (
    !!element &&
    !!element.dataset &&
    !!element.dataset.timecodeStart &&
    !!element.dataset.timecodeEnd &&
    element.className.includes('word')
  )
}

export function isPunctuationElement(element: HTMLElement | null): boolean {
  return (
    !!element &&
    !!element.dataset &&
    !!element.dataset.punctuation &&
    element.className.includes('word')
  )
}

export function getBrowser() {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.indexOf('safari') != -1) {
    if (ua.indexOf('chrome') > -1) {
      return 'Chrome'
    } else {
      return 'Safari'
    }
  }
}

export function getWordIndex(el: HTMLElement): number {
  return !!el && isWordElement(el) ? parseInt(el.dataset.index as string) : -1
}

export function getWordPartIndex(el: HTMLElement): number {
  return !!el && isWordElement(el)
    ? parseInt(el.dataset.partIndex as string)
    : -1
}

export function getWordId(el: HTMLElement): string {
  return !!el && isWordElement(el) ? el.dataset.id || '' : ''
}

export interface Timecode {
  start: Milliseconds
  end: Milliseconds
}

export function getWordTimecodes(word: HTMLElement | null): Timecode {
  if (word && isWordElement(word)) {
    return {
      start: parseInt(word.dataset.timecodeStart as string),
      end: parseInt(word.dataset.timecodeEnd as string)
    }
  }

  return {
    start: -1,
    end: -1
  }
}

// Clears words after a specific word
export function clearPartFrom(element: HTMLElement) {
  if (!isWordElement(element)) {
    return
  }

  const part = element.parentElement

  if (!part || !part.className.includes('sentence')) {
    return
  }

  Array.from(part.children).forEach(word => {
    if (
      isWordElement(word as HTMLElement) &&
      getWordTimecodes(word as HTMLElement).start >=
        getWordTimecodes(element).start
    ) {
      part.removeChild(word)
      return
    }
  })
}

export function getPartSlice(
  element: HTMLElement | null,
  cursorPos: number
): {
  prevSlice: HTMLElement[]
  nextSlice: HTMLElement[]
} {
  const def = {
    prevSlice: [],
    nextSlice: []
  }
  if (!element) {
    return def
  }

  if (!isWordElement(element)) {
    if (!element.className.includes('space')) {
      return def
    }

    if (element.nextElementSibling) {
      element = element.nextElementSibling as HTMLElement
    } else if (element.previousElementSibling) {
      element = element.previousElementSibling as HTMLElement
    } else {
      return def
    }
  }

  const part = element.parentElement
  if (!part || !part.className.includes('sentence')) {
    return def
  }

  const idx = Array.from(part.children).findIndex(word => {
    if (
      getWordTimecodes(word as HTMLElement).start ===
      getWordTimecodes(element).start
    ) {
      return true
    }
  })

  const prevSlice = Array.from(part.children).slice(0, idx) as HTMLElement[]
  const nextSlice = Array.from(part.children).slice(
    idx,
    Array.from(part.children).length - 1
  ) as HTMLElement[]

  if (!prevSlice.length) {
    return def
  }

  if (cursorPos > 1) {
    const word: HTMLElement | undefined = nextSlice.shift()

    if (word) {
      const firstWord = word.cloneNode(true) as HTMLElement
      const secondWord = word.cloneNode(true) as HTMLElement

      const wordText = word.innerText.trim()
      firstWord.innerText = wordText.slice(0, cursorPos)
      secondWord.innerText = wordText.slice(cursorPos, wordText.length)

      prevSlice.push(firstWord)
      nextSlice.unshift(secondWord)
    }
  }

  // if (cursorPos === element.innerText.trim().length) {
  //     prevSlice.shift()
  // }

  return {
    prevSlice: prevSlice.map(n => n.cloneNode(true)) as HTMLElement[],
    nextSlice: nextSlice.map(n => n.cloneNode(true)) as HTMLElement[]
  }
}

export function wordElementTextNode(element: HTMLElement): Node | undefined {
  return element && element.childNodes
    ? Array.from(element.childNodes).find(node => isTextNode(node))
    : undefined
}

export function areSameWords(word1: Word, word2: Word) {
  return (
    word1.start === word2.start &&
    word1.end === word2.end &&
    word1.text === word2.text
  )
}

export const getUniqueElements = (arr: string[]) =>
  arr.filter((v, i, a) => a.indexOf(v) === i)

export const mapTagToUI = (tag: TopicTag, visible = false): UITag => ({
  ...tag,
  apiId: tag.id,
  isVisible: false,
  title: tag.subCategory
})

export function mapTagsToUI(tags: TopicTag[], visible = false): UITag[] {
  return tags.map(tag => mapTagToUI(tag, visible))
}

export const mapTopicToUI = (topic: Topic): UITopic => ({
  ...topic,
  isActive: false,
  title: topic.sub_category
})

export const mapTagsToCategories = (
  tags: UITag[],
  colors: TopicCategoryColors,
  categories: TagCategory[]
): TagCategory[] => {
  return getUniqueElements(tags.map(t => t.category)).map(
    (category: string, index) => ({
      id: category + index || 'Ungrouped' + index,
      title: category || 'Ungrouped',
      color: colors[category.toLowerCase()],
      isOpen: categories[index] ? categories[index].isOpen : true,
      topics: [],
      tags: groupSimilarTags(tags.filter(t => t.category === category))
    })
  )
}

export interface TopicCategoryColors {
  [name: string]: string
}

export const mapTopicsToCategories = (
  topics: UITopic[]
): [TagCategory[], TopicCategoryColors] => {
  const colors: TopicCategoryColors = {}
  const categoryMap = new Map<string, UITopic[]>()

  topics.forEach(topic => {
    const { category, type, sub_category, project_id, client_id } = topic
    const key = `${type}|${sub_category}`

    if (!categoryMap.has(category)) {
      categoryMap.set(category, [])
    }

    const uniqueTopics = categoryMap.get(category)
    const existingTopicIndex = uniqueTopics.findIndex(
      t => `${t.type}|${t.sub_category}` === key
    )

    if ((project_id && client_id) || existingTopicIndex === -1) {
      if (existingTopicIndex > -1) {
        if (
          !uniqueTopics[existingTopicIndex].project_id ||
          !uniqueTopics[existingTopicIndex].client_id
        ) {
          uniqueTopics[existingTopicIndex] = topic
        }
      } else {
        uniqueTopics.push(topic)
      }
    }
  })

  const categories: TagCategory[] = Array.from(categoryMap.entries()).map(
    ([category, uniqueTopics], index) => {
      const color = tagsColorPalette[index % tagsColorPalette.length]
      colors[category.toLowerCase()] = color

      return {
        id: index + 1,
        title: category || 'Ungrouped',
        color,
        isOpen: false,
        tags: [],
        topics: uniqueTopics
      }
    }
  )
  return [categories, colors]
}

export const mapAnalysisTagsToCategories = (
  tags: UITag[],
  colors: TopicCategoryColors,
  categories: TagCategory[]
): TagCategory[] => {
  return getUniqueElements(tags.map(t => t.category)).map(
    (category, index) => ({
      id: index + 1,
      title: category || 'Ungrouped',
      color: colors[category] || 'gray',
      isOpen: categories[index] ? categories[index].isOpen : true,
      tags: groupSimilarAnalysisTags(tags.filter(t => t.category === category)),
      topics: []
    })
  )
}

export const mapTextAnalysisItemToGlossaryOfTermsItem = (
  item: TextAnalysisItem
): GlossaryOfTermsItem => ({
  sub_category: item.sub_category,
  definition: item.definition
})

const GLOSSARY_OF_TERMS_CATEGORY_LIST = [
  'open',
  'understand',
  'personal',
  'exchange',
  'agree',
  'introduction',
  'clarity',
  'generate options',
  'move to action',
  'close',
  'goal setting',
  'reality check',
  'options',
  'willing',
  'introduction',
  'personal',
  'professional',
  'clinical',
  'close'
]

export const GLOSSARY_OF_TERMS_METRIC = 'DNA'

export const checkIsGlossaryOfTermsCategory = (
  category: string,
  list = GLOSSARY_OF_TERMS_CATEGORY_LIST
) => list.includes(category.toLowerCase())

// export function debounce(fn: Function, delay: number) {
//     let timeoutID: number | undefined
//     return function() {
//         clearTimeout(timeoutID)
//         const args = arguments
//         const that = this
//         timeoutID = setTimeout(function() {
//             fn.apply(that, args)
//         }, delay)
//     }
// }
